import { memo } from "react";
import { rgbToCss } from "utils/rgbToCss";
import { ROUTE_STROKE_WIDTH } from "../CircuitPathsLayer/constants";
import { getCircleSize } from "../utils";
import {
  CREATION_RGB,
  HOVER_LINE_DASH_ARRAY,
  NODE_RADIUS_FACTOR,
} from "../RoutesCreationLayer/constants";
import type { DraftDrawingRenderProps } from "./types";

export const DraftDrawingRender = memo(function DraftDrawingRender({
  points,
  hoverPosition,
  zoomLevel,
  circleSizeScale,
}: DraftDrawingRenderProps) {
  if (points.length === 0) return null;

  const color = rgbToCss(CREATION_RGB);
  const size = getCircleSize(zoomLevel) * circleSizeScale;
  const pointRadius = (size / 2) * NODE_RADIUS_FACTOR;

  const pathD = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];

  return (
    <g>
      <path
        d={pathD}
        stroke={color}
        strokeWidth={ROUTE_STROKE_WIDTH}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {hoverPosition && (
        <line
          x1={lastPoint.x}
          y1={lastPoint.y}
          x2={hoverPosition.x}
          y2={hoverPosition.y}
          stroke={color}
          strokeWidth={ROUTE_STROKE_WIDTH}
          strokeDasharray={HOVER_LINE_DASH_ARRAY}
          strokeOpacity={0.6}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {hoverPosition && points.length >= 2 && (
        // Closure preview: the polygon will implicitly close back to the first point.
        <line
          x1={hoverPosition.x}
          y1={hoverPosition.y}
          x2={firstPoint.x}
          y2={firstPoint.y}
          stroke={color}
          strokeWidth={ROUTE_STROKE_WIDTH}
          strokeDasharray={HOVER_LINE_DASH_ARRAY}
          strokeOpacity={0.6}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {points.map((point, index) => (
        <circle
          key={`draft-point-${index}`}
          cx={point.x}
          cy={point.y}
          r={pointRadius}
          fill={color}
          fillOpacity={0.85}
          stroke={color}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
});
